import React, { useEffect } from 'react';
import clsx from 'clsx';

import { Spinner } from 'shared/ui/spinner';
import { NoItemsError } from 'shared/ui/errors';
import { useInfiniteScroll } from 'shared/lib/utils';
import { RealtimeCallback } from 'shared/lib/realtime';
import { useDeleteAssignmentMutation } from 'shared/api/assignment-service';
import { useClasses } from 'entities/classes';
import { useRealtimeAssignments } from 'entities/assignments';
import { ScheduledAssignmentsList } from 'widgets/scheduled-assignments';
import { useUserProgramsData } from 'widgets/programs/model';
import { useAssignmentActionTimestampHandlers } from 'features/assignment-panel';

import { useInfiniteScheduledAssignments } from './lib';
import { PageWrapper } from './page-wrapper';
import styles from './styles.module.scss';

export const ScheduledAssignmentsPage = () => {
  const { classes } = useClasses();
  const classesCount = classes?.length ?? 0;
  const shouldShowClassFilter = classesCount > 1;

  const { userProgramsData } = useUserProgramsData();
  const shouldShowProgramsFilter = userProgramsData.length > 1;

  const {
    assignmentsToDisplay,
    isAssignmentsLoading,
    isAssignmentsFetching,
    isAllAssignmentsShown,
    loadMoreAssignments,
    refetchScheduledAssignments,
    currentClassesKeys,
  } = useInfiniteScheduledAssignments();

  const { isLoadMoreRefVisible, setLoadMoreRef } = useInfiniteScroll();

  const [deleteAssignment] = useDeleteAssignmentMutation();

  const { writeAssignmentActionTimestamp } =
    useAssignmentActionTimestampHandlers();

  useEffect(() => {
    if (isLoadMoreRefVisible && !isAllAssignmentsShown) {
      loadMoreAssignments();
    }
  }, [isLoadMoreRefVisible, isAllAssignmentsShown, loadMoreAssignments]);

  const handleAssignmentsUpdatesRealtime: RealtimeCallback = (_, payload) => {
    const { isDeleted, startDate, ampAssignmentKey, isFromEducatorHome } =
      payload;

    // do not re-fetch assignments if realtime update is coming from Educator Home
    // as it will be done via query invalidation after PUT or DELETE request
    if (isFromEducatorHome) return;

    const isScheduledAssignment =
      !!startDate && new Date(startDate).getTime() > Date.now();

    if (isScheduledAssignment && isDeleted) {
      if (ampAssignmentKey) {
        deleteAssignment(ampAssignmentKey)
          .unwrap()
          .then(() => {
            // invalidate get class assignments query
            writeAssignmentActionTimestamp();
          });
      }
    }

    refetchScheduledAssignments();
  };

  useRealtimeAssignments({
    assignmentAssignedCallback: refetchScheduledAssignments,
    assignmentUpdatedCallback: handleAssignmentsUpdatesRealtime,
    classesKeys: currentClassesKeys,
  });

  const shouldShowLoadMoreSpinner =
    isAssignmentsFetching &&
    !isAllAssignmentsShown &&
    !!assignmentsToDisplay?.length;

  if (isAssignmentsLoading) {
    return (
      <PageWrapper
        showClassFilter={shouldShowClassFilter}
        showProgramsFilter={shouldShowProgramsFilter}
      >
        <div
          className={clsx('position-relative w-100', styles.spinnerContainer)}
        >
          <Spinner isDark isLarge testId="scheduled-spinner" />
        </div>
      </PageWrapper>
    );
  }

  if (!assignmentsToDisplay?.length) {
    return (
      <PageWrapper
        showClassFilter={shouldShowClassFilter}
        showProgramsFilter={shouldShowProgramsFilter}
      >
        <NoItemsError />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      showClassFilter={shouldShowClassFilter}
      showProgramsFilter={shouldShowProgramsFilter}
    >
      <ScheduledAssignmentsList assignments={assignmentsToDisplay} />
      <div className="position-relative" ref={setLoadMoreRef}>
        {shouldShowLoadMoreSpinner && (
          <div
            className={clsx('position-relative w-100', styles.spinnerContainer)}
          >
            <Spinner isDark isLarge testId="scheduled-spinner" />
          </div>
        )}
      </div>
    </PageWrapper>
  );
};
